import {makeStyles} from '@material-ui/core'
import React, {Fragment, useCallback, useMemo, useState} from 'react'
import {AutoComplete} from '../../Component/AutoComplete'
import {PeriodPicker} from '../../Component/PeriodPicker'
import {city as cityGeo} from 'ca-plateform-geozone-domain'
import {city as cityPlat} from 'ca-plateform-plateform-commons'
import {Option} from "../../Component/Select";
import {MultiSelectBox} from "../../Component/MultiSelect";

const useStyles = makeStyles(() => ({
  filter: {
    width: '200px',
    marginRight: '10px',
    ['@media (max-width: 800px)']: {
      width: '100% !important',
      marginRight: '0 !important'
    },
    '& .MuiOutlinedInput-root': {
      height: '44px !important'
    },
    '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)':
      {
        marginTop: '0px'
      },
    '& .MuiInputLabel-filled ': {
      transform: ' translate(12px, 25px) scale(1)'
    }
  },
  icon: {
    color: '#9a9a9a',
    cursor: 'pointer'
  }
}))

export interface Territory
  extends cityGeo.colisactiv.platform.geozone.model.Territory {}
export interface TerritoryPage
  extends cityPlat.colisactiv.platform.commons.Page<Territory> {}
export interface DateInterval
  extends cityPlat.colisactiv.platform.commons.DateInterval {}

export interface DashboardFiltersModel {
  startPeriod?: DateInterval
  endPeriod?: DateInterval
  status?: string
  operatorIds?: string[]
  carrierIds?: string[]
  territories?: Territory[]
}

interface DashboardFiltersProps {
  operators: Option[]
  carriers: Option[]
  allowedTerritories?: Nullable<string[]>
  filterValues: DashboardFiltersModel
  getTerritories: (
    page: number,
    size: number,
    name: string
  ) => Promise<TerritoryPage>
  onFilterChange: (values: DashboardFiltersModel) => void
  canSeeOperatorFilter: boolean
  canSeeCarrierFilter: boolean
  canSeeTerritoryFilter: boolean
}

export const DashboardFilters = (props: DashboardFiltersProps) => {
  const {
    filterValues,
    onFilterChange,
    carriers,
    operators,
    allowedTerritories = null,
    getTerritories,
    canSeeCarrierFilter,
    canSeeOperatorFilter,
    canSeeTerritoryFilter
  } = props
  const classes = useStyles()
  const [selectedStartPeriod, setStartSelectedPeriod] = useState<
    DateInterval | undefined
  >(
    filterValues?.startPeriod?.startDate == null
      ? undefined
      : {
          startDate: filterValues.startPeriod.startDate!,
          endDate: filterValues.startPeriod.endDate!
        }
  )

  const [selectedEndPeriod, setEndSelectedPeriod] = useState<
    DateInterval | undefined
  >(
    filterValues?.endPeriod?.startDate == null
      ? undefined
      : {
          startDate: filterValues.endPeriod.startDate!,
          endDate: filterValues.endPeriod.endDate!
        }
  )

  const handleStartPeriodChange = useCallback(
    (period: DateInterval | undefined) => {
      setStartSelectedPeriod(period)

      onFilterChange({
        ...filterValues,
        startPeriod: period,
      })
    },
    [onFilterChange, filterValues]
  )

  const handleEndPeriodChange = useCallback(
    (period: DateInterval | undefined) => {
      setEndSelectedPeriod(period)

      onFilterChange({
        ...filterValues,
        endPeriod: period
      })
    },
    [onFilterChange, filterValues]
  )

  const handleOperatorsChange = useCallback(
    (operatorIds?: string[]) => {
      onFilterChange({
        ...filterValues,
        operatorIds: operatorIds?.length === 0 ? undefined : operatorIds,
      })
    },
    [onFilterChange, filterValues]
  )

  const handleCarriersChange = useCallback(
    (carriers?: string[]) => {
      onFilterChange({
        ...filterValues,
        carrierIds: carriers?.length == 0 ? undefined : carriers,
      })
    },
    [onFilterChange, filterValues]
  )

  const handleTerritoriesChange = useCallback(
    (territory: Territory | Territory[]) => {
      const next = Array.isArray(territory) ? territory : [territory]
      const filtered = allowedTerritories
        ? next.filter((t) => allowedTerritories.includes(t.code))
        : next
      onFilterChange({
        ...filterValues,
        territories: filtered.length === 0 ? undefined : filtered
      })
    },
    [onFilterChange, filterValues, allowedTerritories]
  )

  const [autocompleteText, setAutocompleteText] = useState(
    'Rechercher un territoire'
  )
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<Territory[]>(
    []
  )

  const handleSearchTerritory = useCallback(async (search: string) => {
    if (search.trim().length === 0) {
      setAutoCompleteOptions([])
      setAutocompleteText('Rechercher un territoire')
      return
    }

    try {
      const response = await getTerritories(0, 5, search.trim())
      let territories = response.list
      if (allowedTerritories) {
        territories = response.list.filter((territory: Territory) =>
          allowedTerritories.includes(territory.code)
        )
      }
      setAutoCompleteOptions(territories)
      if (response.list.length === 0) {
        setAutocompleteText('Aucun territoire trouvé')
      }
    } catch (err) {
      console.error(err)
      setAutoCompleteOptions([])
      setAutocompleteText('Une erreur est survenue, veuillez réessayer')
    }
  }, [])

  const getTerritoryName = useCallback((territory: Territory) => {
    return territory.name
  }, [])

  // The MUI Autocomplete is controlled via the `value` prop; passing a fresh `[]` on every render
  // (when no territory is selected) would reset the input on each keystroke because each render's
  // empty array is a new reference. Memoize so the reference is stable.
  const selectedTerritories = useMemo(
    () => filterValues.territories ?? [],
    [filterValues.territories]
  )

  return (
    <Fragment>
      <PeriodPicker
        id='filterPeriod'
        placeholder={'Période de début'}
        period={selectedStartPeriod}
        onDateChange={handleStartPeriodChange}
        inputClassName={classes.filter}
        clearable
      />
      <PeriodPicker
        id='filterPeriod'
        placeholder={'Période de fin'}
        period={selectedEndPeriod}
        onDateChange={handleEndPeriodChange}
        inputClassName={classes.filter}
        clearable
      />
      {canSeeOperatorFilter && (
        <MultiSelectBox
          onChange={handleOperatorsChange}
          options={operators}
          values={filterValues.operatorIds}
          label={''}
          placeHolder={'Opérateur(s)'}
          className={`${classes.filter} project-filters`}
          id='dashboard-filters-operator'
        />
      )}
      {canSeeCarrierFilter && (
        <MultiSelectBox
          onChange={handleCarriersChange}
          options={carriers}
          values={filterValues.carrierIds}
          label={''}
          placeHolder={'Transporteur(s)'}
          className={`${classes.filter} project-filters`}
          id='dashboard-filters-carrier'
        />
      )}
      {canSeeTerritoryFilter && (
        <AutoComplete<Territory>
          id='dashboard-filters-territory'
          multiple
          onChangeSelectedElement={handleTerritoriesChange}
          options={autoCompleteOptions}
          defaultValue={selectedTerritories}
          label=''
          onSearch={handleSearchTerritory}
          noOptionsText={autocompleteText}
          getOptionLabel={getTerritoryName}
          getOptionSelected={(option, value) => option.code === value.code}
          placeholder='Territoire(s)'
          className={classes.filter}
          readonly={allowedTerritories ? allowedTerritories.length == 1 : false}
        />
      )}
    </Fragment>
  )
}
